// The qampo-ui-library DatePicker works in Date objects while bookings store ISO
// date strings (YYYY-MM-DD). These convert between the two using LOCAL date
// components — never toISOString(), which renders UTC midnight and shifts the day
// backwards for timezones behind UTC, landing bookings on the wrong date.
export function isoStringToDate(isoDate: string): Date | undefined {
  if (!isoDate) return undefined;
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

export function dateToIsoString(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Day arithmetic via local date components (see the note above) so the result
// never shifts a day across timezones. An unparsable input is returned as-is.
export function addDaysToIsoDate(isoDate: string, days: number): string {
  const date = isoStringToDate(isoDate);
  if (!date) return isoDate;
  date.setDate(date.getDate() + days);
  return dateToIsoString(date);
}

export function formatDateStr(isoDate?: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1)}`;
}

// Intl.DateTimeFormat.format() throws a RangeError on an Invalid Date, and the router has no
// errorElement — one malformed date would blank the page rather than one label.
const INVALID_DATE_PLACEHOLDER = '—';

// Numeric date in the active locale's own field order.
//
// Callers pass two different backend shapes. Some send a LocalDateTime that carries a time of day
// (audit-log createdAt, year-plan lastModifiedDate); others send a date-only LocalDate
// (treatment-plan week ranges, EconomicsGroupRow completedAt). There is no timeZone override, so
// both render from local components — which means a bare YYYY-MM-DD, parsed as UTC midnight, shows
// the previous day to a viewer behind UTC. That is the behaviour this function has always had and
// REH-262 deliberately leaves it alone; weekDateUtils pins timeZone: 'UTC' because its inputs are
// uniformly UTC-midnight dates, which is not true here.
export function formatDate(isoDate: string, locale: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return INVALID_DATE_PLACEHOLDER;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  }).format(date);
}

// Supplies the offset that a zone-less timestamp is missing, treating it as UTC.
//
// The backend serialises these as java.time.LocalDateTime ("2026-03-05T09:12:00") from a container
// that runs UTC — nothing pins TZ in rehabplanner-deployment, so the JVM default applies. Left alone,
// `new Date()` reads an offset-less datetime as *browser*-local, which in Denmark renders the moment
// 1-2 hours early; for a timestamp whose whole purpose is telling the planner how fresh a
// calculation is, that is worse than showing nothing.
//
// The durable fix is an offset-carrying type on the wire (Instant/OffsetDateTime). Until then this is
// the honest reading of what the backend actually sends.
function withAssumedUtcOffset(isoDateTime: string): string {
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(isoDateTime);
  return hasOffset ? isoDateTime : `${isoDateTime}Z`;
}

// Fixed dd/mm/yy HH:mm — not localized, unlike formatDate — for timestamps where the time of day
// matters (e.g. when a calculation finished). Rendered in the viewer's local zone.
export function formatDateTime(isoDateTime: string): string {
  const date = new Date(withAssumedUtcOffset(isoDateTime));
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export function getCurrentIsoWeek(): number {
  const now = new Date();
  const jan4 = new Date(now.getFullYear(), 0, 4);
  const start = new Date(jan4.getTime() - ((jan4.getDay() || 7) - 1) * 86_400_000);
  return Math.ceil(((now.getTime() - start.getTime()) / 86_400_000 + 1) / 7);
}

export function formatDateRangeSmart(startIso: string, endIso: string, locale: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const includeYear = start.getFullYear() !== end.getFullYear();
  const options: Intl.DateTimeFormatOptions = includeYear
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short' };
  const formatter = new Intl.DateTimeFormat(locale, options);
  return `${formatter.format(start)} – ${formatter.format(end)}`;
}